import React, { FC, useRef } from 'react';
import * as Yup from 'yup';
import './form.css';
import {
    FeedBackType,
    Alert,
    PageLoader,
    MessageHelper,
    ActionStatusHelper,
    AppEnum,
    Formik,
    FormikProps,
    Form,
    SubmitButton,
    UIFeedbackHooks,
    useUIFeedback,
    UIFeedback,
    useDomainForm,
    UIAction,
    useBreakPoint,
    UIButton,
    ActionResponse,
    GenericDomainFormProps,
} from '@/Shared';
import { AccessRightModel } from '@/Domains';


export interface FormHeaderLink {
    href: string,
    label: string,
    tooltip?: string
    className?: string
}

export interface DomainFormHook {
    accessRight: AccessRightModel,
    response: ActionResponse,
    formResponse: ActionResponse,
    entityFormVM: any,
    handleSubmit: (values: Record<string, any>) => Promise<void>;
    viewEnums?: Record<string, AppEnum[]>
    ValidationSchema: Yup.ObjectSchema<Record<string, any>>
    uiFeedbackHooks: UIFeedbackHooks,
    isModalMode?: boolean,
    extraParams?: any,
    requestId?: number,
    computeUIActions? : () => UIAction[]
}


/**
 * =====================================================
 * Component: GenericDomainForm
 * =====================================================
 *
 * Generic wrapper for any domain-specific form.
 * Handles the orchestration layer while leaving form-specific rendering
 * to the domain's form component (DomainForm).
 *
 * Responsibilities:
 * - Resolve domain configuration and hooks dynamically
 * - Enforce access rights and handle loading/error states
 * - Provide global feedback layers: Toast, ConfirmModal, Modal, Alert
 * - Inject sub-action buttons generated via domain helpers
 * - Mount the domain form and pass all necessary props
 *
 * Author: Samano CASTRE
 * Date: February 15, 2026
 */
const GenericDomainForm: FC<GenericDomainFormProps> = ({
    domainType,
    inModalHook,
    extraArgs
}) => {

    /** Ref for scrollable forms or field validation purposes */
    const formRef = useRef<HTMLFormElement>(null);

    const uiFeedbackHooks = useUIFeedback();

    const {isDesktop} = useBreakPoint();

    const {
        domainFormHook,
        DomainForm,
        domainLabel,
        UIActions,
        response
    } = useDomainForm(
        domainType,
        uiFeedbackHooks,
        inModalHook,
        extraArgs
    );

    const { 
        accessRight, 
        entityFormVM, 
        ValidationSchema, 
        handleSubmit, 
        formResponse 
    } = domainFormHook;

   
    /** Guard in case DomainForm is not configured for this domain */
    if (!DomainForm) {
        return (
            <Alert
                data={{
                    message: `Aucun formulaire n'a été défini pour ce domaine : (${domainLabel})`,
                    type: FeedBackType.ERROR
                }}
            />
        );
    }


    /**
     * Render loader while permissions are being fetched.
     * Ensures the form does not appear until access rights are resolved.
     */
    if (!accessRight) {
        return (
            <PageLoader
                message="Chargement des permissions..."
            />
        );
    }

    /** Guard for write access; show error if user cannot edit this domain */
    if (!accessRight.canWrite(domainType)) {
        return (
            <Alert
                data={{
                    message: "Vous n'avez pas les droits pour accéder à ce formulaire.",
                    type: FeedBackType.ERROR
                }}
            />
        );
    }

    /** Check for fetch or form response errors */
    const { isLoading, isFailed, isIdle } = ActionStatusHelper.is(response.status);

    // Handle fetch or form response errors
    if (isFailed) {
        return (
            <Alert
                data={{
                    message: response.error || MessageHelper.FETCHING.ERROR(domainLabel),
                    type: FeedBackType.ERROR
                }}
            />
        );
    }

    /** Display loader while domain form data is being fetched */
    if (isLoading || isIdle || !entityFormVM) {
        return (
            <PageLoader
                message={MessageHelper.FETCHING.LOADING(domainLabel)}
            />
        );
    }


    /**
     * Render domain form with global UI feedback layers.
     * Responsibilities:
     * - Toast, ConfirmModal, Modal, Alert
     * - Form header with optional link
     * - Sub-action buttons
     * - Submit button respecting form state
     */
    return (
        <div className="GenericDomainForm form fade-in" data-testid="GenericDomainForm">

            {/* Form header with optional link */}
            <div className="form-header">
                <h3>{entityFormVM.title}</h3>
                {
                    entityFormVM.headerLink && (
                        <a
                            className={entityFormVM.headerLink.className ?? ""}
                            href={entityFormVM.headerLink.href}
                            title={entityFormVM.headerLink.tooltip}
                            aria-label={
                                entityFormVM.headerLink.tooltip ??
                                `Lien : ${entityFormVM.headerLink.label}`
                            }
                        >
                            {entityFormVM.headerLink.label}
                        </a>
                    )
                }

            </div>

            {/* Formik wrapper for handling form state and validation */}
            <Formik<any>
                initialValues={entityFormVM.formValues ?? {}}
                enableReinitialize={true}
                validationSchema={ValidationSchema}
                onSubmit={handleSubmit}
            >

                {(formikProps: FormikProps<any>) =>
                    <Form className='form-body' ref={formRef}>

                        {/* Render sub-action buttons */}
                        <div className="sub-actions flex-row"> 
                        {
							UIActions.sub.map(action =>
									<UIButton
										key={action.key}
										action={action}
										isDesktop={isDesktop}
									/>
							)
						}
                        
                        </div>

                        {/**
                         * Global UI feedback layers
                         *
                         * These components are mounted once at the detail level
                         * and are triggered imperatively by domain logic.
                         */}
                        <UIFeedback uiFeedbackHooks={uiFeedbackHooks}/>

                        {/* Render the domain-specific form */}
                        <DomainForm
                            domainFormHook={domainFormHook}
                            formRef={formRef}
                            formikProps={formikProps}
                        />

                        {/* Submit button reflecting form validity and loading state */}
                        <SubmitButton
                            disabled={!formikProps.dirty || !formikProps.isValid}
                            isLoading={ActionStatusHelper.isLoading(formResponse.status)}
                        />
                    </Form>
                }
            </Formik>

        </div>
    );
}

export default GenericDomainForm;